import * as React from "react";

import type { AuditLogEntry } from "api/auditLogs";
import {
  Badge,
  Button,
  CodeValue,
  CopyButton,
  DescriptionList,
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  type DescriptionListItem,
} from "Components/ui";
import { ShieldCheck } from "Components/ui/icons";
import { cn } from "utils/cn";
import { formatDateTime, formatINR, formatRelative } from "utils/format";
import {
  formatActionLabel,
  formatSource,
  getActionMeta,
  isSystemEntry,
  MONEY_FIELDS,
} from "./actionMeta";
import { isPaymentEntry, PaymentSummary } from "./PaymentSummary";

type PlainObject = Record<string, unknown>;

const isPlainObject = (value: unknown): value is PlainObject =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const hasKey = (value: PlainObject, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const formatValue = (value: unknown): string => {
  if (value === "") {
    return '""';
  }
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value, null, 2) ?? String(value);
};

const toJson = (value: unknown): string =>
  value === undefined ? "null" : JSON.stringify(value, null, 2) ?? String(value);

interface DiffRow {
  key: string;
  before: unknown;
  after: unknown;
  hasBefore: boolean;
  hasAfter: boolean;
  changed: boolean;
}

/** Top-level field comparison; null when either side isn't an object (shown as raw JSON only). */
const diffSnapshots = (before: unknown, after: unknown): DiffRow[] | null => {
  const left = before === null || before === undefined ? {} : before;
  const right = after === null || after === undefined ? {} : after;
  if (!isPlainObject(left) || !isPlainObject(right)) {
    return null;
  }
  const keys = Array.from(new Set(Object.keys(left).concat(Object.keys(right)))).sort((a, b) =>
    a.localeCompare(b)
  );
  return keys.map((key) => {
    const hasBefore = hasKey(left, key);
    const hasAfter = hasKey(right, key);
    return {
      key,
      before: left[key],
      after: right[key],
      hasBefore,
      hasAfter,
      changed: hasBefore !== hasAfter || JSON.stringify(left[key]) !== JSON.stringify(right[key]),
    };
  });
};

/** First non-empty value of `keys` in the after snapshot, then the before one (login IP etc.). */
const pickContextValue = (entry: AuditLogEntry, keys: string[]): unknown => {
  const sides: unknown[] = [entry.changes?.after, entry.changes?.before];
  let found: unknown;
  sides.some((side) => {
    if (!isPlainObject(side)) {
      return false;
    }
    const key = keys.find((candidate) => {
      const value = side[candidate];
      return value !== undefined && value !== null && value !== "";
    });
    if (key) {
      found = side[key];
      return true;
    }
    return false;
  });
  return found;
};

const formatLocation = (value: unknown): string | null => {
  if (value === undefined) {
    return null;
  }
  if (typeof value === "string") {
    return value;
  }
  if (isPlainObject(value)) {
    const parts = ["city", "region", "country"]
      .map((key) => value[key])
      .filter((part): part is string => typeof part === "string" && part.length > 0);
    if (parts.length > 0) {
      return parts.join(", ");
    }
  }
  return JSON.stringify(value);
};

const ValueBlock: React.FC<{
  value: unknown;
  present: boolean;
  tone: "before" | "after" | "same";
  /** Paise: shown in rupees, with the stored value underneath. */
  money?: boolean;
}> = ({ value, present, tone, money = false }) => {
  if (!present) {
    return <span className="px-2 text-subtle-foreground">—</span>;
  }
  const rupees = money && typeof value === "number" ? formatINR(value) : null;
  return (
    <div
      className={cn(
        "max-h-48 overflow-auto whitespace-pre-wrap break-all rounded-md px-2 py-1 font-mono text-xs",
        tone === "before" && "bg-danger-subtle text-danger-text",
        tone === "after" && "bg-success-subtle text-success-text",
        tone === "same" && "text-muted-foreground"
      )}
    >
      {rupees ? (
        <>
          <span className="font-sans text-sm font-medium">{rupees}</span>
          <span className="block opacity-70">{formatValue(value)} paise</span>
        </>
      ) : (
        formatValue(value)
      )}
    </div>
  );
};

const FieldComparison: React.FC<{ rows: DiffRow[]; money?: boolean }> = ({
  rows,
  money = false,
}) => {
  const [showUnchanged, setShowUnchanged] = React.useState(false);
  const changedRows = rows.filter((row) => row.changed);
  const unchangedCount = rows.length - changedRows.length;
  const visible = showUnchanged ? rows : changedRows;

  if (rows.length === 0) {
    return <p className="text-sm text-muted-foreground">No field values were recorded.</p>;
  }

  return (
    <div className="space-y-3">
      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">No fields changed.</p>
      ) : (
        <div className="divide-y divide-border">
          <div
            aria-hidden="true"
            className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_minmax(0,1fr)] gap-3 pb-2 text-xs text-muted-foreground"
          >
            <span>Field</span>
            <span>Before</span>
            <span>After</span>
          </div>
          {visible.map((row) => (
            <div
              key={row.key}
              className="grid grid-cols-[minmax(0,8rem)_minmax(0,1fr)_minmax(0,1fr)] items-start gap-3 py-2"
            >
              <span className="break-all pt-1 font-mono text-xs text-foreground">{row.key}</span>
              <ValueBlock
                value={row.before}
                present={row.hasBefore}
                tone={row.changed ? "before" : "same"}
                money={money && MONEY_FIELDS.has(row.key)}
              />
              <ValueBlock
                value={row.after}
                present={row.hasAfter}
                tone={row.changed ? "after" : "same"}
                money={money && MONEY_FIELDS.has(row.key)}
              />
            </div>
          ))}
        </div>
      )}
      {unchangedCount > 0 && (
        <Button variant="link" size="sm" onClick={() => setShowUnchanged((current) => !current)}>
          {showUnchanged ? "Hide unchanged fields" : `Show ${unchangedCount} unchanged`}
        </Button>
      )}
    </div>
  );
};

const RawBlock: React.FC<{ label: string; value: unknown }> = ({ label, value }) => {
  const json = toJson(value);
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <CopyButton value={json} label={`Copy ${label.toLowerCase()} JSON`} />
      </div>
      <pre className="max-h-72 overflow-auto whitespace-pre-wrap break-all rounded-control border border-border bg-surface-muted p-3 font-mono text-xs leading-relaxed text-foreground">
        {json}
      </pre>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-3">
    <h3 className="text-sm font-medium text-foreground">{title}</h3>
    {children}
  </section>
);

export interface AuditLogDetailSheetProps {
  /** Kept after closing so the panel doesn't empty while it slides out. */
  entry: AuditLogEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Read-only view of one audit entry. Entries are immutable, so there is nothing to edit. */
export const AuditLogDetailSheet: React.FC<AuditLogDetailSheetProps> = ({
  entry,
  open,
  onOpenChange,
}) => {
  const rows = React.useMemo(
    () => (entry ? diffSnapshots(entry.changes?.before, entry.changes?.after) : null),
    [entry]
  );

  if (!entry) {
    return null;
  }

  const meta = getActionMeta(entry.action);
  const ip = pickContextValue(entry, ["ip", "ipAddress", "ip_address"]);
  const location = formatLocation(pickContextValue(entry, ["location", "geo"]));

  const system = isSystemEntry(entry);
  const billing = /^BILLING_/i.test(entry.action || "");
  const details: DescriptionListItem[] = [
    system
      ? { label: "Actor", value: "System", hint: formatSource(entry.source) }
      : {
          label: "Actor",
          value: entry.actorName || (entry.actorId ? "Unnamed user" : null),
          hint: entry.actorId ? (
            <CodeValue value={entry.actorId} label="actor id" truncate />
          ) : undefined,
        },
    {
      label: "Target",
      value: entry.targetName || (entry.targetId ? "Unnamed target" : null),
      hint: entry.targetId ? (
        <CodeValue value={entry.targetId} label="target id" truncate />
      ) : undefined,
    },
    {
      label: "Time",
      value: formatDateTime(entry.timestamp),
      hint: formatRelative(entry.timestamp),
    },
    {
      label: "Entry id",
      value: <CodeValue value={entry._id} label="entry id" truncate />,
    },
  ];
  if (ip !== undefined) {
    details.push({
      label: "IP address",
      value: <CodeValue value={formatValue(ip)} label="IP address" />,
    });
  }
  if (location) {
    details.push({ label: "Location", value: location });
  }

  const integrity: DescriptionListItem[] = [
    {
      label: "Entry hash",
      value: entry.hash ? (
        <CodeValue value={entry.hash} label="entry hash" truncate />
      ) : (
        <span className="text-muted-foreground">Not recorded</span>
      ),
    },
    {
      label: "Previous hash",
      value: entry.prevHash ? (
        <CodeValue value={entry.prevHash} label="previous hash" truncate />
      ) : (
        <span className="text-muted-foreground">None</span>
      ),
    },
    { label: "Hash version", value: entry.hashVersion },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent size="lg">
        <SheetHeader
          title={formatActionLabel(entry.action)}
          description={`${
            system ? `System · ${formatSource(entry.source)}` : entry.actorName || "Unknown actor"
          } · ${formatRelative(entry.timestamp)}`}
        />
        <SheetBody className="space-y-8">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={meta.tone} dot>
              {meta.label}
            </Badge>
            <CodeValue value={entry.action || ""} label="action name" />
          </div>

          <DescriptionList items={details} columns={2} />

          {isPaymentEntry(entry) && (
            <Section title="Payment">
              <PaymentSummary entry={entry} />
            </Section>
          )}

          <Section title="Changes">
            <Tabs key={entry._id} defaultValue={rows ? "compare" : "raw"}>
              <TabsList variant="pill">
                <TabsTrigger value="compare" disabled={!rows}>
                  Compare
                </TabsTrigger>
                <TabsTrigger value="raw">Raw JSON</TabsTrigger>
              </TabsList>
              {rows && (
                <TabsContent value="compare">
                  <FieldComparison rows={rows} money={billing} />
                </TabsContent>
              )}
              <TabsContent value="raw" className="space-y-4">
                <RawBlock label="Before" value={entry.changes?.before} />
                <RawBlock label="After" value={entry.changes?.after} />
              </TabsContent>
            </Tabs>
          </Section>

          <Section title="Integrity">
            <DescriptionList items={integrity} columns={1} />
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <ShieldCheck aria-hidden="true" className="mt-px h-4 w-4 shrink-0 text-success" />
              Audit entries are append-only and hash-chained, so they can't be edited or deleted.
            </p>
          </Section>
        </SheetBody>
        <SheetFooter>
          <SheetClose asChild>
            <Button variant="secondary">Close</Button>
          </SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
